import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useEffect } from "react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../providers/form";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import ComboBox from "widgets/shared/inputs/combobox";
import GenericButton from "widgets/controls/buttons/generic";
import { shallowEqual, useSelector } from "react-redux";

import Checkbox from "widgets/shared/inputs/checkbox";
import { usePermissions } from "hooks/usePermissions";
// Schema
const UserEditSchema = z.object({
  // name: z.string().readonly(),
  // family_name: z.string().readonly(),
  // email: z.string().readonly(),
  role: z.string(),
});

export type UserEditFormType = z.infer<typeof UserEditSchema>;

// Props
type UserEditFormProps = {
  submit: (data: UserEditFormType) => void;
  onClose: () => void;
  defaultValues?: UserEditFormType;
  values?: UserEditFormType;
  loading?: boolean;
};

const EditUserForm = ({
  submit,
  onClose,
  defaultValues,
  values,
  loading,
}: UserEditFormProps) => {
  const initialRole = useRef<string | undefined>(undefined);
  
  const form = useForm<UserEditFormType>({
    resolver: zodResolver(UserEditSchema),
    defaultValues: {
      // name: "",
      // family_name: "",
      // email: "",
      role: "",
    },
    values,
  });

  // Track the initial role value
  useEffect(() => {
    if (defaultValues?.role && initialRole.current === undefined) {
      initialRole.current = defaultValues.role;
    }
  }, [defaultValues?.role]);

  // Get current role value
  const currentRole = form.watch("role");
  const hasRoleChanged = currentRole && currentRole !== initialRole.current;

  const { t } = useTranslation();
  const { availableRoles } = usePermissions();
  const { errors, isDirty } = form.formState;

  const onSubmit = async (formData: UserEditFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const clearErrors = () => {
    form.clearErrors();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className=" space-y-6">
        <div className="mt-[6px] grid grid-cols-1 gap-3 ">
          <FormField
            control={form.control}
            name="role"
            rules={{ required: false }}
            render={({ field }) => (
              <FormItem>
                {/* <FormLabel>{t("form.input.family.name")}</FormLabel> */}
                <FormControl>
                  <div className="grid w-full grid-cols-2 gap-4">
                    {availableRoles().map((item, index) => (
                      <Checkbox
                        key={`checkbox-${item.label}-${index}`}
                        id={`checkbox-${item.label}`}
                        color="white"
                        labelKey={item.label}
                        checked={field.value === item.value}
                        valueKey={item.value}
                        onChange={() => field.onChange(item.value)}
                      />
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex-grow"></div>
        {hasRoleChanged && (
          <div className="flex w-full flex-col pt-2">
            <div className="flex justify-end">
              <GenericButton
                type="submit"
                text={t("generic.action.save.changes")}
                disabled={!isDirty}
                loading={loading || false}
                size="md"
                color="secondary"
              />
            </div>
          </div>
        )}
      </form>
    </Form>
  );
};

export default EditUserForm;
